import { useNuxtApp, useRuntimeConfig } from '#app'
import { getUserOrganizationName } from '~/utils/authv2.js'
import {
  FeatureRole,
  ConfigFlag,
  type FeatureRoleValue,
  type ConfigFlagValue,
} from './feature-roles'

/**
 * Pure feature check with explicit roles and config.
 */
export function checkFeaturePure(
  roles: string[],
  config: Record<string, unknown>,
  featureName: FeatureRoleValue,
  configFlag: ConfigFlagValue | null = null
): boolean {
  const featureRoles = (roles || []).filter((r) =>
    r.toLowerCase().includes('feature')
  )
  const hasFeatureRole =
    featureRoles.some((f) => f.toLowerCase() === featureName.toLowerCase()) ||
    !config.checkKeycloakFeaturesPermissions
  return configFlag
    ? hasFeatureRole && Boolean(config[configFlag])
    : hasFeatureRole
}

function getKeycloakRoles(): string[] {
  return useNuxtApp().$keycloak?.tokenParsed?.realm_access?.roles || []
}

export function hasFeatureRole(featureName: FeatureRoleValue): boolean {
  return getKeycloakRoles().some(
    (role) => role.toLowerCase() === featureName.toLowerCase()
  )
}

/**
 * Reads roles and config from the Nuxt app.
 */
function checkFeature(
  featureName: FeatureRoleValue,
  configFlag: ConfigFlagValue | null = null
): boolean {
  const roles = getKeycloakRoles()
  const runtimeConfig = useRuntimeConfig()
  const publicConfig =
    (runtimeConfig.public as Record<string, unknown> | undefined) || {}

  return checkFeaturePure(roles, publicConfig, featureName, configFlag)
}

export function enableGlossaryView(): boolean {
  return checkFeature(FeatureRole.Glossary, ConfigFlag.Glossaries)
}

export function enableTranslationMemoryView(): boolean {
  return checkFeature(FeatureRole.TranslationMemory, ConfigFlag.VectorSearch)
}

export function enableResourceCreationView(): boolean {
  return hasFeatureRole(FeatureRole.ResourceCreation)
}

export function enableDictionariesView(): boolean {
  return checkFeature(FeatureRole.Dictionary, ConfigFlag.Dictionaries)
}

export function enableTextTranslatorView(): boolean {
  return checkFeature(FeatureRole.TextTranslator)
}

export function enableLanguageCorrectionView(): boolean {
  return checkFeature(FeatureRole.TextPolisher, ConfigFlag.LanguageCorrection)
}

export function enableDocumentTranslatorView(): boolean {
  return checkFeature(FeatureRole.DocumentTranslator)
}

export function enableQualityEvaluationView(): boolean {
  return checkFeature(
    FeatureRole.QualityEvaluation,
    ConfigFlag.QualityEvaluation
  )
}

/**
 * Checks the underscore-separated role format (`"Terminology_Manager_" + org_name`; only the
 * fixed prefix is underscored — spaces inside a multi-word org name are left as-is). Compared
 * case-insensitively.
 */
function hasGlobalTerminologyManagerRoleForOrganization(
  orgName: string | null
): boolean {
  const roles = getKeycloakRoles()

  if (!orgName) return false
  const org = orgName.toLowerCase()
  const globalTerminologyManagerRole = `terminology_manager_${org}`
  return roles.some(
    (role) => role.toLowerCase() === globalTerminologyManagerRole
  )
}

export function buildTerminologyManagerAccessByGroupId(
  organizations: Array<{
    id?: string | number | null
    name?: string | null
  }> = []
): Record<string, boolean> {
  return organizations.reduce<Record<string, boolean>>(
    (accessByGroupId, org) => {
      if (org?.id) {
        accessByGroupId[String(org.id)] =
          hasGlobalTerminologyManagerRoleForOrganization(org.name || null)
      }

      return accessByGroupId
    },
    {}
  )
}

/**
 * Checks the underscore-separated role format (`"Tenant_Admin_" + org_name`; only the fixed
 * prefix is underscored — spaces inside a multi-word org name are left as-is). Compared
 * case-insensitively, so the exact capitalization doesn't matter.
 */
function hasTenantAdminRoleForOrganization(orgName: string | null): boolean {
  const roles = getKeycloakRoles()

  if (!orgName) return false
  const org = orgName.toLowerCase()
  const tenantAdminRole = `tenant_admin_${org}`
  return roles.some((role) => role.toLowerCase() === tenantAdminRole)
}

export function buildTenantAdminAccessByGroupId(
  organizations: Array<{
    id?: string | number | null
    name?: string | null
  }> = []
): Record<string, boolean> {
  return organizations.reduce<Record<string, boolean>>(
    (accessByGroupId, org) => {
      if (org?.id) {
        accessByGroupId[String(org.id)] = hasTenantAdminRoleForOrganization(
          org.name || null
        )
      }

      return accessByGroupId
    },
    {}
  )
}

export function showUsageLimits(): boolean {
  const config = useRuntimeConfig().public as Record<string, unknown>
  return Boolean(config[ConfigFlag.Limits])
}

export function enableRetentionPolicyView(): boolean {
  const config = useRuntimeConfig().public as Record<string, unknown>
  return Boolean(config[ConfigFlag.RetentionPolicy])
}

export async function isGlobalTerminologyManager(): Promise<boolean> {
  const orgName = await getUserOrganizationName()
  return hasGlobalTerminologyManagerRoleForOrganization(orgName)
}
